<script setup lang="ts">
import type { EntriesQueryVariables, GlossaryThumbFragment, PageGlossaryArchiveEntryFragment } from '#graphql-operations'
import { useInfiniteQuery } from '@tanstack/vue-query'

interface ArchiveGlossaryProps {
  data: PageGlossaryArchiveEntryFragment
}

defineProps<ArchiveGlossaryProps>()

const letters: string[] = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))

const search = useScopedRouteQuery('q', '')
const selectedLetters = useScopedRouteQuery<string[], string[]>('letter', [], {
  transform: value => (Array.isArray(value) ? value : [value]),
})

watch(selectedLetters, (value) => {
  if (value.length > 1)
    selectedLetters.value = [value[value.length - 1] as string]
}, { flush: 'sync' })

const letter = computed(() => selectedLetters.value[0] ?? '')

const variables = computed<EntriesQueryVariables>(() => ({
  section: ['glossary'],
  search: search.value || undefined,
  title: letter.value ? [`${letter.value}*`] : undefined,
  orderBy: 'title ASC',
}))

const queryKey = computed(() => ['glossary', variables.value])

const {
  status,
  data: pages,
  error,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  refetch,
  suspense,
} = useInfiniteQuery({
  queryKey,
  queryFn: ctx => fetchServerPage<GlossaryThumbFragment>(24, ctx.pageParam as number, variables.value),
  getNextPageParam: lastGroup => lastGroup.nextOffset,
  initialPageParam: 0,
})

await suspense()

const count = computed(() => pages.value?.pages[0]?.count ?? 0)
const entries = computed(() => (pages.value ? pages.value.pages.flatMap(page => page.rows) : []))

interface GlossarySection {
  key: string
  heading: string
  headingClass: string
  items: GlossaryThumbFragment[]
}

// A search matches on title & text, so results can't be regrouped by first letter;
// shown as a single section instead.
const activeHeading = computed<Pick<GlossarySection, 'heading' | 'headingClass'> | null>(() => {
  if (search.value)
    return { heading: search.value, headingClass: 'typo-serif-3' }
  if (letter.value)
    return { heading: letter.value, headingClass: 'typo-serif-4 uppercase' }
  return null
})

const sections = computed<GlossarySection[]>(() => {
  if (search.value) {
    return [{ key: 'search', ...activeHeading.value!, items: entries.value }]
  }

  const grouped = Object.groupBy(entries.value, entry => entry.title?.charAt(0).toUpperCase() ?? '')

  return Object.entries(grouped).map(([char, items]) => ({
    key: char,
    heading: char,
    headingClass: 'typo-serif-4 uppercase',
    items: items ?? [],
  }))
})

function reset() {
  selectedLetters.value = []
  search.value = ''
}
</script>

<template>
  <Section class="pb-25">
    <header class="layout-grid mt-6.5 gap-y-6.5 lg:mt-10 lg:gap-y-16">
      <Text v-if="data.heading" class="typo-serif-4 text-center col-span-full" :html="data.heading" />
      <SearchField v-model="search" class="col-span-full lg:col-start-5 lg:col-end-9" :label="$t('type')" />
      <div class="col-span-full space-y-2.5">
        <div class="py-1 flex max-w-full of-x-auto">
          <CheckboxGroupField v-model="selectedLetters" class="mx-auto" :wrap="true" name="letter" :label="$t('filter_by_letter')" hide-label>
            <CheckboxButton
              v-for="char in letters"
              :key="char"
              :value="char"
              :label="char"
            />
          </CheckboxGroupField>
        </div>
        <div class="text-center min-h-6">
          <ButtonLink
            v-show="letter || search"
            :title="$t('reset')"
            @click="reset"
          />
        </div>
      </div>
    </header>

    <div class="container mt-10 lg:mt-20">
      <div v-if="status === 'pending'" class="layout-grid">
        <div class="col-span-3">
          <Text v-if="activeHeading" as="h3" class="top-$header-height sticky" :class="activeHeading.headingClass" :text="activeHeading.heading" />
        </div>
        <ul class="6xl:grid-cols-4 gap-2.5 grid col-span-9 lg:grid-cols-2 xl:grid-cols-3">
          <li v-for="index in 9" :key="index" class="p-4 rounded-2 bg-ivory">
            <div class="opacity-60 space-y-5 lg:p-6 lg:space-y-8">
              <Skeleton :index class="h-4 max-w-32" />
              <Skeleton :index="index + 1" class="h-12" />
            </div>
          </li>
        </ul>
      </div>
      <div v-else-if="status === 'error' || error" class="text-center space-y-5">
        <Text as="div" class="typo-sans-1 uppercase" :text="$t('an_error_occurred_while_retrieving_entry', { label: $t('result', 2) })" />
        <Button theme="primary" :title="$t('retry')" @click="() => refetch()" />
      </div>
      <template v-else-if="status === 'success'">
        <div v-if="count" class="space-y-10 lg:space-y-15">
          <div v-for="section in sections" :id="`glossary-${section.key}`" :key="section.key" class="layout-grid">
            <div class="col-span-3">
              <Text as="h3" class="top-$header-height sticky" :class="section.headingClass" :text="section.heading" />
            </div>
            <ul class="6xl:grid-cols-4 gap-2.5 grid col-span-9 lg:grid-cols-2 xl:grid-cols-3">
              <li v-for="entry in section.items" :key="entry.slug">
                <CardGlossary :data="entry" />
              </li>
            </ul>
          </div>
        </div>
        <Text v-else as="div" class="typo-sans-1 text-center uppercase" :text="$t('there_are_no_entry_to_show', { label: $t('result', 2) })" />
      </template>
    </div>

    <ArchivePagination
      v-if="hasNextPage"
      :is-fetching-next-page="isFetchingNextPage"
      label="result"
      :current-count="entries.length"
      :count="count"
      @fetch-next-page="fetchNextPage"
    />
  </Section>
</template>
